/**
 * Nav — router di navigazione state-driven.
 *
 * Un'unica regola: OGNI entry della history del browser porta con sé il
 * proprio layer in history.state ({tyIdx, tyLayer, tyBase}). La history del
 * browser è la sola fonte di verità: avanti e indietro sono lo stesso
 * percorso — al popstate si legge il layer di destinazione e lo si
 * (ri)renderizza tramite restorePreviousLayer (mobile.js). Niente stack da
 * tenere sincronizzato a mano, niente popstate "tecnici" da sopprimere.
 *
 * Layer:
 *  - pagine: 'home', 'search', 'saved', 'profile', 'download',
 *    'video-<id>', 'collection-<id>', 'searchin-…', 'page-<id>'
 *  - modali: 'modal-*' / 'modal_playlist' — entry come le altre, ma al
 *    ritorno si chiude solo il visual (la pagina sotto è ancora renderizzata)
 *    e navigando AVANTI non vengono mai riaperte (si mostra la loro base).
 *
 * URL: su web (TY_WEB) ogni layer ha il suo path SEO reale (/video?w=…,
 * /collection?s=…, /search, …); su nativo il solo hash '#layer'. Le modali
 * non cambiano mai l'URL sul web.
 *
 * `currentPageState` (all_scr_config.js) resta lo specchio [radice…corrente]
 * per le letture legacy (top/length); le scritture passano SOLO da Nav.
 */
var Nav = (function () {
    var stack = currentPageState;  // specchio per le letture legacy
    var idx = -1;                  // posizione corrente nella history (-1 = pre-boot)
    var rendered = null;           // pagina attualmente a schermo (le modali non la cambiano)
    var navlock = false;           // true durante un render da popstate: scritture ignorate
    var pendingBack = 0;           // back() in volo: operazioni accodate fino al popstate
    var queued = [];

    function top() { return stack.length ? stack[stack.length - 1] : undefined; }
    function isModal(name) { return !!name && name.indexOf('modal') === 0; }

    /* --------------------------- URL --------------------------------- */
    // Path SEO assoluti sul web (la <base href="/capp/"> non deve influire);
    // i parametri di servizio dello sviluppo locale vengono preservati.
    function urlFor(name) {
        if (!window.TY_WEB) return '#' + name;
        var q = new URLSearchParams();
        try {
            var cur = new URLSearchParams(window.location.search);
            // anche i parametri del loader nativo: senza channel_id/app_scheme
            // un reload dell'app riparte dal selettore canale
            ['channel_id', 'capweb', 'apilocal', 'app_scheme', 'app_version', 'apple_store_id', 'play_store_id'].forEach(function (k) {
                if (cur.get(k)) q.set(k, cur.get(k));
            });
        } catch (e) {}
        var path = '/';
        if (name.indexOf('video-') === 0) { path = '/video'; q.set('w', name.slice(6)); }
        else if (name.indexOf('collection-') === 0) { path = '/collection'; q.set('s', name.slice(11)); }
        else if (name.indexOf('page-') === 0) { path = '/page'; q.set('id_custom', name.slice(5)); }
        else if (name.indexOf('searchin') === 0 || name === 'search') {
            path = '/search';
            // 'searchin-<cat>-<tag>-<name>' (appHeader.js): categoria/tag nell'URL
            // così la pagina è condivisibile e riapribile da web.js
            var sp = name.split('-');
            var cat = (sp[1] && sp[1] !== 'null') ? sp[1] : '';
            var tag = (sp[2] && sp[2] !== 'null') ? sp[2] : '';
            if (cat || tag) {
                if (cat) q.set('idCat', cat);
                if (tag) q.set('idTag', tag);
                var label = sp.slice(3).join('-');
                if (label && label !== 'null') q.set('name', label);
            }
        }
        else if (name.indexOf('playlist-') === 0) { path = '/playlist'; q.set('id', name.slice(9)); }
        else if (name === 'saved') { path = '/saved'; }
        else if (name === 'profile') { path = '/profile'; }
        else if (name.indexOf('checkout') === 0) {
            path = '/checkout';
            var cid = name.split('-')[1];
            if (cid) q.set('id', cid);
        }
        else if (name === 'giftcard' || name === 'giftcard_redeem' || name === 'thankyou' || name === 'payments') { path = '/' + name; }
        // 'home', 'download' e ogni altro layer restano su '/'
        var qs = q.toString();
        return path + (qs ? '?' + qs : '');
    }

    // URL da scrivere per una NUOVA entry: le modali non toccano l'URL
    function urlForEntry(name) {
        if (window.TY_WEB && isModal(name)) {
            return window.location.pathname + window.location.search;
        }
        return urlFor(name);
    }

    /* -------------------------- modali -------------------------------- */
    function selectorFor(name) {
        if (!name) return null;
        if (name === 'modal_playlist') return '#modal_playlist';
        if (name.indexOf('modal-') === 0) return '#' + name;
        return null;
    }

    function closeVisuals(name) {
        var sel = selectorFor(name);
        if (!sel) return;
        try { BSLight.ModalHide(sel); } catch (e) {}
        if (name === 'modal-video-comment') {
            try { $('#modal-video-comment-txt-chat').hide(); } catch (e) {}
        }
        cleanupBackdrops();
    }

    // chiude TUTTE le modali aperte (BSLight le marca con la classe 'in');
    // usata da navigateToPage: una pagina nuova copre e azzera ogni modal
    function closeAllModals() {
        try {
            document.querySelectorAll('.modal.in').forEach(function (m) {
                if (m.id) { try { BSLight.ModalHide('#' + m.id); } catch (e) {} }
            });
        } catch (e) {}
        try { $('#modal-video-comment-txt-chat').hide(); } catch (e) {}
        cleanupBackdrops();
    }

    // backdrop orfani: se BSLight non ha più modali aperte non devono esisterne
    function cleanupBackdrops() {
        try {
            if (typeof Modals !== 'undefined' && Modals.length === 0) {
                document.querySelectorAll('.modal-backdrop').forEach(function (b) { b.remove(); });
                document.body.classList.remove('modal-open');
            }
        } catch (e) {}
    }

    /* ------------------------- scritture ------------------------------ */

    // Primo layer del boot: adotta l'entry di history già esistente (dopo un
    // reload può portare un tyIdx di una sessione precedente) SENZA
    // riscriverne l'URL — quello canonico arriva alla prima navigazione vera.
    function adopt(name) {
        var st = window.history.state || {};
        idx = (typeof st.tyIdx === 'number') ? st.tyIdx : 0;
        stack.length = idx + 1;
        for (var i = 0; i < idx; i++) { if (stack[i] === undefined) stack[i] = ''; }
        stack[idx] = name;
        rendered = name;
        try {
            window.history.replaceState({ tyIdx: idx, tyLayer: name }, '',
                window.location.pathname + window.location.search + window.location.hash);
        } catch (e) {}
    }

    function push(name) {
        if (navlock) return;
        if (pendingBack > 0) { queued.push(function () { push(name); }); return; }
        if (idx === -1) { adopt(name); return; }
        if (top() === name) return;           // niente duplicati adiacenti
        idx++;
        stack.length = idx + 1;
        stack[idx] = name;
        var st = { tyIdx: idx, tyLayer: name };
        if (isModal(name)) { st.tyBase = rendered; } else { rendered = name; }
        window.history.pushState(st, '', urlForEntry(name));
    }

    function replaceTop(name) {
        if (navlock) return;
        if (pendingBack > 0) { queued.push(function () { replaceTop(name); }); return; }
        if (idx === -1) { adopt(name); return; }
        if (top() === name) return;
        stack[idx] = name;
        var st = { tyIdx: idx, tyLayer: name };
        if (isModal(name)) { st.tyBase = rendered; } else { rendered = name; }
        window.history.replaceState(st, '', urlForEntry(name));
    }

    // Guard sul back: stati "visuali" che un indietro deve chiudere PRIMA di
    // diventare una traversata di history (es. il fullscreen del player, che
    // si chiude restando nella pagina video). Il primo guard che ritorna true
    // consuma il back. Registrati dai moduli che possiedono quello stato, così
    // Nav non deve conoscerli.
    var backGuards = [];
    function addBackGuard(fn) {
        if (typeof fn === 'function' && backGuards.indexOf(fn) === -1) { backGuards.push(fn); }
    }

    // Indietro di una entry. Ritorna false se siamo alla radice (l'app può
    // uscire; sul web si lascia semplicemente il sito).
    function back() {
        for (var i = 0; i < backGuards.length; i++) {
            try { if (backGuards[i]()) return true; } catch (e) {}
        }
        if (idx <= 0) return false;
        pendingBack++;
        window.history.back();
        return true;
    }

    // Back condizionato (ex dismissTop): torna indietro solo se il layer
    // corrente corrisponde (nome esatto o prefisso). Chiusure e ri-render
    // avvengono nel popstate, come per ogni altro back.
    function backIf(expected) {
        var cur = top();
        if (!cur || idx <= 0) return false;
        if (expected && cur !== expected && cur.indexOf(expected) !== 0) return false;
        return back();
    }

    // Esegue fn quando l'eventuale back in volo è stato consumato (subito se
    // non ce ne sono): per aprire modali/pagine DOPO un backIf senza correre.
    function after(fn) {
        if (pendingBack > 0) { queued.push(fn); } else { fn(); }
    }

    /* --------------- lettura della history: unico render --------------- */
    function onPopState(e) {
        // entry senza tyIdx = navigazione per frammento non creata da Nav
        // (es. un <a href="#"> sfuggito al guard sotto): senza questo il
        // layer diventava la radice e il back successivo chiudeva l'app.
        // Si scarta l'entry tornando a quella corrente, senza ri-render.
        if (!e.state || typeof e.state.tyIdx !== 'number') {
            pendingBack++;
            window.history.back();
            return;
        }
        if (pendingBack > 0) pendingBack--;
        var st = e.state;
        var tIdx = (typeof st.tyIdx === 'number') ? st.tyIdx : 0;
        var target = st.tyLayer || stack[tIdx] || 'home';
        var from = top();
        var goingBack = (tIdx < idx);

        // specchio riallineato alla nuova posizione
        idx = tIdx;
        stack.length = idx + 1;
        for (var i = 0; i < idx; i++) { if (stack[i] === undefined) stack[i] = ''; }
        stack[idx] = target;

        // si lascia una modale: si chiude solo il suo visual
        if (isModal(from)) { closeVisuals(from); }

        if (isModal(target)) {
            if (goingBack && isModal(from)) {
                // back da una modale impilata: la modale sotto è ancora
                // aperta e resta il layer corrente — nessun render
            } else {
                // le entry-modale non si riaprono mai: valgono come la base
                target = st.tyBase || rendered || 'home';
                stack[idx] = target;
            }
        }

        // URL canonico dell'entry su cui si è atterrati (sistema anche la
        // radice adottata al boot con l'URL del deep-link)
        if (!isModal(target)) {
            try { window.history.replaceState({ tyIdx: idx, tyLayer: target }, '', urlFor(target)); } catch (err) {}
        }

        try { console.log('[nav] goto "' + target + '" (da "' + (from || '') + '") idx=' + idx); } catch (err) {}

        if (target !== rendered && !isModal(target)) {
            // fra una pagina e l'altra si riparte in portrait (solo nativo);
            // il lock può fallire su desktop/fuori fullscreen: va zittito
            if (!window.TY_WEB) {
                try {
                    var lockP = screen.orientation.lock('portrait');
                    if (lockP && typeof lockP.catch === 'function') lockP.catch(function () {});
                } catch (err) {}
            }
            navlock = true;
            try {
                if (typeof restorePreviousLayer === 'function') { restorePreviousLayer(target, from); }
                rendered = target;
            } finally {
                navlock = false;
            }
        }

        // operazioni arrivate mentre il back era in volo
        while (pendingBack === 0 && queued.length) { queued.shift()(); }
    }

    window.addEventListener('popstate', onPopState);

    // rete di sicurezza per eventuali <a href="#">: il default aggiungerebbe
    // un'entry '#' alla history e con <base href="/capp/"> (shell web e app
    // nativa) navigherebbe su /capp/ → 404 → errorPath (downloads.html).
    // Le tab di video e collection non hanno più href: il click lo gestisce
    // l'onclick del <li>, l'<a> resta solo per lo stile. Vale per ogni runtime.
    document.addEventListener('click', function (e) {
        var a = e.target && e.target.closest ? e.target.closest('a[href="#"]') : null;
        if (a) { e.preventDefault(); }
    });

    // lo scroll nelle traversate è gestito dall'app (currentPageDepth):
    // quello automatico del browser confliggerebbe con i re-render
    try { window.history.scrollRestoration = 'manual'; } catch (e) {}

    return {
        push: push,
        replaceTop: replaceTop,
        back: back,
        addBackGuard: addBackGuard,
        backIf: backIf,
        dismissTop: backIf,        // compat col vecchio nome
        after: after,
        closeVisuals: closeVisuals,
        closeAllModals: closeAllModals,
        top: top,
        urlFor: urlFor,
        depth: function () { return idx + 1; }
    };
})();
